// Legal pages — Privacy Policy, Terms of Service, SMS Consent, HIPAA Notice.
// These are production-grade boilerplate for a U.S. telehealth platform.
// Replace effective dates and counsel-reviewed clauses prior to launch.
import { useEffect } from "react";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/shared/SEO";

const EFFECTIVE = "February 19, 2026";
const COMPANY = "BestRx.online";
const CONTACT_EMAIL = "care@bestrx.online";
const CONTACT_PHONE = "(214) 815-5966";
const PRIVACY_EMAIL = "privacy@bestrx.online";

function LegalShell({ title, eyebrow, path, description, children }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO title={title} path={path} description={description} />
      <Nav />
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute -top-20 -left-40 w-[480px] h-[480px] rounded-full blur-3xl bg-[var(--leen-blue)]/20" />
        <div className="relative px-5 md:px-8 lg:px-12 max-w-[900px] mx-auto">
          <p className="eyebrow text-[var(--leen-pink)] mb-3">{eyebrow}</p>
          <h1 className="font-display text-[42px] sm:text-[64px] md:text-[88px] leading-[0.92] tracking-tight">
            {title.toUpperCase()}
          </h1>
          <p className="mt-5 text-[13px] text-[var(--leen-ink-mute)]">
            Effective {EFFECTIVE} · {COMPANY}
          </p>
        </div>
      </section>
      <section className="py-10 md:py-14">
        <div className="px-5 md:px-8 lg:px-12 max-w-[900px] mx-auto prose-legal">
          {children}
        </div>
      </section>
      <Footer />
    </main>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-display text-[22px] md:text-[28px] mt-10 mb-4 text-white">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-[15px] leading-relaxed text-[var(--leen-ink-soft)] mb-4">{children}</p>
);
const Ul = ({ children }) => (
  <ul className="list-disc pl-6 text-[15px] leading-relaxed text-[var(--leen-ink-soft)] mb-4 space-y-2">{children}</ul>
);

export function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy Policy"
      eyebrow="Legal"
      path="/privacy"
      description="BestRx Privacy Policy — how we collect, use, store, and share your personal and protected health information."
    >
      <P>
        {COMPANY} ("BestRx," "we," "us," or "our") respects your privacy. This Privacy Policy
        explains how we collect, use, disclose, and safeguard your personal information and
        Protected Health Information ("PHI") when you visit our website, complete an intake, or
        use our telehealth services.
      </P>

      <H2>1. Information We Collect</H2>
      <Ul>
        <li>Identifiers: name, address, email, phone number, date of birth.</li>
        <li>Health intake data: medical history, current medications, symptoms, goals, photos you submit.</li>
        <li>Payment information processed by PCI-compliant third-party processors (we do not store card numbers).</li>
        <li>Device, browser, IP, and usage data via cookies, pixels, and analytics SDKs.</li>
      </Ul>

      <H2>2. How We Use Your Information</H2>
      <Ul>
        <li>To evaluate eligibility for clinically-supervised programs.</li>
        <li>To connect you with a licensed clinician and dispensing pharmacy.</li>
        <li>To process payments and arrange shipment of prescribed medications.</li>
        <li>To send transactional, appointment, and program-related communications.</li>
        <li>To send marketing communications where you have opted in (see SMS Consent).</li>
        <li>To improve the platform, prevent fraud, and comply with applicable law.</li>
      </Ul>

      <H2>3. How We Share Information</H2>
      <P>
        We share information with: (a) licensed clinicians who review your intake; (b) partnered
        compounding and dispensing pharmacies; (c) shipping carriers; (d) payment processors;
        (e) technology vendors operating under written contracts (and Business Associate
        Agreements where PHI is involved); and (f) regulators or law enforcement where required
        by law. We do not sell your personal information.
      </P>

      <H2>4. Your Choices and Rights</H2>
      <P>
        Depending on your state of residence, you may have the right to access, correct, delete,
        or restrict use of your personal information, and to opt out of certain marketing
        communications. To exercise these rights, contact {PRIVACY_EMAIL}.
      </P>

      <H2>5. Data Security</H2>
      <P>
        We use administrative, technical, and physical safeguards aligned with HIPAA and
        industry best practices, including encryption in transit and at rest, access controls,
        logging, and routine reviews. No method of transmission is 100% secure.
      </P>

      <H2>6. Children's Privacy</H2>
      <P>The platform is intended for adults 18+. We do not knowingly collect information from minors.</P>

      <H2>7. Updates</H2>
      <P>
        We may update this Policy from time to time. Material changes will be posted with a new
        effective date and, where required, notified to you directly.
      </P>

      <H2>8. Contact</H2>
      <P>Email: {PRIVACY_EMAIL} · Phone: {CONTACT_PHONE}</P>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell
      title="Terms of Service"
      eyebrow="Legal"
      path="/terms"
      description="BestRx Terms of Service — the agreement governing your use of the BestRx telehealth platform."
    >
      <P>
        These Terms of Service ("Terms") govern your use of {COMPANY} (the "Service"). By using
        the Service, you agree to these Terms. If you do not agree, do not use the Service.
      </P>

      <H2>1. Eligibility</H2>
      <P>You must be 18 years or older, a U.S. resident, and able to enter a binding contract.</P>

      <H2>2. Telehealth & Medical Disclaimer</H2>
      <P>
        BestRx is a technology platform that facilitates connections between patients and
        independent licensed clinicians and dispensing pharmacies. BestRx is not a medical
        provider or pharmacy. No prescription is guaranteed; treatment is at the sole discretion
        of the reviewing clinician based on your intake and applicable standards of care.
      </P>
      <P>
        The Service is not for medical emergencies. If you are experiencing an emergency, call
        911 or go to the nearest emergency room.
      </P>

      <H2>3. Account & Honest Disclosure</H2>
      <P>
        You agree to provide complete and accurate information during intake. Withholding or
        misrepresenting medical information may result in denial of service and could be unsafe.
      </P>

      <H2>4. Payments & Refunds</H2>
      <Ul>
        <li>Program fees are charged at enrollment. Medication fees are charged when dispensed.</li>
        <li>Consultation fees are non-refundable once a clinician has reviewed your intake.</li>
        <li>Compounded medications are made-to-order and generally non-refundable after dispense, except where required by law or carrier damage.</li>
      </Ul>

      <H2>5. Shipping</H2>
      <P>
        We ship via licensed carriers. Delivery times are estimates. Title and risk transfer
        upon carrier hand-off; we will work with you on lost or damaged shipments.
      </P>

      <H2>6. Acceptable Use</H2>
      <P>
        You agree not to misuse the Service, attempt to access another user's account, or use
        the Service to violate any law, including resale or diversion of medications.
      </P>

      <H2>7. Intellectual Property</H2>
      <P>
        All content, marks, and software on the Service are the property of {COMPANY} or its
        licensors and may not be copied or distributed without permission.
      </P>

      <H2>8. Limitation of Liability</H2>
      <P>
        To the fullest extent permitted by law, BestRx, its affiliates, officers, directors,
        employees, and agents will not be liable for indirect, incidental, special,
        consequential, or punitive damages. Total liability is capped at the amounts you paid to
        BestRx in the 12 months preceding the claim.
      </P>

      <H2>9. Arbitration & Class-Action Waiver</H2>
      <P>
        Any dispute will be resolved by binding arbitration on an individual basis. You waive
        the right to a jury trial and to participate in a class action, except where prohibited
        by law.
      </P>

      <H2>10. Changes</H2>
      <P>We may update these Terms. Continued use after changes constitutes acceptance.</P>

      <H2>11. Contact</H2>
      <P>Email: {CONTACT_EMAIL} · Phone: {CONTACT_PHONE}</P>
    </LegalShell>
  );
}

export function SmsConsentPage() {
  return (
    <LegalShell
      title="SMS Consent"
      eyebrow="Messaging Policy"
      path="/sms-consent"
      description="BestRx SMS / text messaging consent disclosure. Message frequency, charges, opt-out and privacy details."
    >
      <P>
        By providing your mobile number to {COMPANY} and opting in, you consent to receive
        recurring text messages from BestRx and its providers and pharmacy partners related to
        your intake, eligibility, appointments, shipping, refill reminders, and care updates.
      </P>

      <H2>Message Frequency</H2>
      <P>Message frequency varies based on your interactions with our care team and pharmacy.</P>

      <H2>Message & Data Rates</H2>
      <P>Message and data rates may apply, as determined by your wireless carrier.</P>

      <H2>Carriers</H2>
      <P>
        Supported carriers include AT&amp;T, T-Mobile, Verizon, Sprint, U.S. Cellular, and other
        major U.S. carriers. Carriers are not liable for delayed or undelivered messages.
      </P>

      <H2>Help</H2>
      <P>Reply HELP to any message for assistance, or contact {CONTACT_EMAIL} / {CONTACT_PHONE}.</P>

      <H2>Opt-Out</H2>
      <P>
        You can opt out at any time by replying STOP to any message. You will receive a final
        confirmation, after which we will not send further marketing messages. Transactional
        messages necessary to operate your account (e.g., shipping notifications) may continue
        unless you cancel your account.
      </P>

      <H2>Privacy</H2>
      <P>
        Information collected through SMS is handled in accordance with our Privacy Policy and
        HIPAA Notice. We do not sell your mobile information.
      </P>

      <H2>Consent Is Not a Condition of Purchase</H2>
      <P>You are not required to consent to SMS messages as a condition of using the Service.</P>
    </LegalShell>
  );
}

export function HipaaPage() {
  return (
    <LegalShell
      title="HIPAA Notice of Privacy Practices"
      eyebrow="HIPAA"
      path="/hipaa"
      description="BestRx HIPAA Notice of Privacy Practices — how Protected Health Information is used, disclosed, and protected."
    >
      <P>
        THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW
        YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE REVIEW IT CAREFULLY.
      </P>

      <H2>Our Responsibilities</H2>
      <P>
        {COMPANY} and the clinicians and pharmacies operating on the platform are required by
        law to maintain the privacy and security of your Protected Health Information ("PHI"),
        notify you of any breach of unsecured PHI, follow this Notice, and not use or disclose
        PHI other than as described here without your written authorization.
      </P>

      <H2>How We May Use & Disclose Your PHI</H2>
      <Ul>
        <li><b>Treatment:</b> share your information with clinicians and pharmacies treating you.</li>
        <li><b>Payment:</b> bill and collect for services rendered.</li>
        <li><b>Health Care Operations:</b> quality improvement, training, audits, and care coordination.</li>
        <li><b>Required by Law:</b> public-health reporting, judicial orders, and regulators.</li>
        <li><b>With Your Authorization:</b> for marketing, certain research, or any other use not covered above.</li>
      </Ul>

      <H2>Your Rights</H2>
      <Ul>
        <li>Inspect and obtain a copy of your PHI.</li>
        <li>Request amendments to PHI you believe is incorrect.</li>
        <li>Request an accounting of certain disclosures.</li>
        <li>Request restrictions on certain uses and disclosures.</li>
        <li>Request confidential communications by alternative means or locations.</li>
        <li>Receive a paper copy of this Notice.</li>
        <li>File a complaint with us or with the U.S. Department of Health &amp; Human Services. You will not be retaliated against for filing a complaint.</li>
      </Ul>

      <H2>Changes to This Notice</H2>
      <P>
        We reserve the right to change this Notice and to make the revised notice effective for
        all PHI we maintain. The current Notice will always be available on our website.
      </P>

      <H2>Contact for HIPAA Concerns</H2>
      <P>Privacy Officer · {PRIVACY_EMAIL} · {CONTACT_PHONE}</P>
    </LegalShell>
  );
}
